import { isEqual, sortBy, unionBy } from 'lodash';
import { FlowItemParameter } from '../../model/flow.types';

/**
 * This function merges multiple flow item parameters into a single array.
 * It applies the differences from right to left, so priority is given to the
 * latter arrays' parameters.
 *
 * e.g. mergeFlowItemParameters([a, b, c]) will merge the parameters in the
 * following order: c, b, a. Where refs of 'b' will be prioritized over 'a'
 * and refs of 'c' will be prioritized over 'b'.
 *
 * @param paramsToMerge The flow item parameter arrays to merge
 * @returns A new array with the merged parameters
 */
export const mergeFlowItemParameters = <T,>(
  paramsToMerge: FlowItemParameter<T>[][]
): FlowItemParameter<T>[] => {
  // to adhere to the lodash typings, we call the apply function to pass in
  // multiple arrays as arguments
  // eslint-disable-next-line prefer-spread
  const mergedParams: FlowItemParameter[] = unionBy(
    paramsToMerge.reverse().flat(),
    'ref'
  );

  return mergedParams;
};

/**
 * This function compares two arrays of flow item parameters to determine if
 * they are equal.
 *
 * @param params1 Array of flow item parameters to compare against
 * @param params2 Array of flow item parameters to compare with
 * @returns boolean indicating whether the parameters are equal
 */
export const compareFlowItemParameters = (
  params1: FlowItemParameter[],
  params2: FlowItemParameter[]
): boolean => {
  if (params1.length !== params2.length) {
    return false;
  }

  const sorted1 = sortBy(params1, 'ref');
  const sorted2 = sortBy(params2, 'ref');

  return isEqual(sorted1, sorted2);
};
